import { useCallback, useContext, useEffect, useRef, useState, memo } from 'react'
import {
    WebSocketResponseMessage,
    WebSocketResponseMessage_CandleSubscriptionResponse,
    WebSocketResponseMessage_CandleSubscriptionResponse_SecurityCandle,
    WebSocketResponseMessage_SecuritySnapshotSubscriptionResponse
} from '../../proto/generated/websocket'
import { subscriptionTags } from '../../assets/constants'
import { Security, SecuritySnapshot, SecuritySnapshots, Segment } from '../../proto/generated/security'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { useDispatch } from 'react-redux'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import { useSelector } from 'react-redux'
import { Position } from '../../proto/generated/position'
import { selectBasketAnalyzeDetails, selectOrdersData } from '../../selectors/orders'
import websocketActions from '../../actions/websocket.actions'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { getSecurityFromCache, getSecurityKey } from '../../util/security'
import { TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { BasketPositionTableRow, customPositionCellStyle, customPositionRowStyle } from './BasketWisePostionContainer'
import { setOrderDataFunction } from '../../reducer/orders.reducer'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { getCurentOrderPricesFromData } from '../../util/tradepanel'

export type Props = {}

const strikePriceOf = (position: Position): number | undefined =>
    position.security ? getSecurityFromCache(getSecurityKey(position.security.exchange, position.security.code))?.strikePrice : undefined

const ReadOnlyBasketWisePositionContainer = () => {
    const [candlesData, setCandlesData] = useState<WebSocketResponseMessage_CandleSubscriptionResponse_SecurityCandle[]>([])
    const [candlesSubscriptionResponse, setCandlesSubscriptionResponse] = useState<WebSocketResponseMessage_CandleSubscriptionResponse[]>([])
    const ws = useContext(WebSocketContext)
    const [securitySnapshots, setSecuritySnapshots] = useState<SecuritySnapshot[]>([])
    const [securitySnapshotsSubscriptionResponse, setSecuritySnapshotSubscriptionResponses] = useState<WebSocketResponseMessage_SecuritySnapshotSubscriptionResponse[]>([])
    const securityCodeToPreviousClose = useRef<Map<string, number>>(new Map<string, number>())
    const [securityCodeToSnapshot, setSecurityCodeToSnapshot] = useState<Map<string, SecuritySnapshot>>(new Map<string, SecuritySnapshot>())
    const basketAnalyzeDetails = useSelector(selectBasketAnalyzeDetails)
    const underlyingToken = basketAnalyzeDetails?.underlyingSecurity ? getSecurityKey(basketAnalyzeDetails?.underlyingSecurity.exchange, basketAnalyzeDetails?.underlyingSecurity.code) : ''
    const optionsExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const dispatch = useDispatch()
    const orderData = useSelector(selectOrdersData)
    const { orders = [] } = orderData
    const basketWisePositions = basketAnalyzeDetails?.basketPositions
    const showGreeks = false

    useEffect(() => {
        const candleUpdate = candlesSubscriptionResponse.find((s) => s.tag === subscriptionTags.READONLY_BASKET_POSITION)
        if (candleUpdate && candleUpdate.securityCandles.length > 0) {
            setCandlesData(candleUpdate.securityCandles)
        }
    }, [candlesSubscriptionResponse])

    const onWebSocketMessageReceived = useCallback((data: Uint8Array) => {
        const responseData = WebSocketResponseMessage.decode(data)
        if (responseData.securitySnapshotSubscriptionResponses.length > 0) {
            setSecuritySnapshotSubscriptionResponses(responseData.securitySnapshotSubscriptionResponses)
        }
        if (responseData.candleSubscriptionResponses.length > 0) {
            setCandlesSubscriptionResponse(responseData.candleSubscriptionResponses)
        }
    }, [])

    useEffect(() => {
        const securitySnapshotUpdate = securitySnapshotsSubscriptionResponse.find((s) => s.tag === subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG)
        if (securitySnapshotUpdate && securitySnapshotUpdate.securitySnapshots.length > 0) {
            const existingSecuritySnapshots = [...securitySnapshots]
            securitySnapshotUpdate.securitySnapshots.forEach((updatedSnapshot) => {
                const index = existingSecuritySnapshots.findIndex((existingSnapshot) => existingSnapshot.security?.code === updatedSnapshot.security?.code)
                if (index !== -1) {
                    const existingSnapshot = existingSecuritySnapshots[index]
                    existingSecuritySnapshots[index] = {
                        ...existingSnapshot,
                        previousClose: securityCodeToPreviousClose.current.get(updatedSnapshot.security!.code) ?? 0,
                        lastTradedPrice: updatedSnapshot.lastTradedPrice
                    }
                } else {
                    const update = {
                        ...updatedSnapshot,
                        previousClose: securityCodeToPreviousClose.current.get(updatedSnapshot.security!.code) ?? 0
                    }
                    existingSecuritySnapshots.push(update)
                }
            })
            setSecuritySnapshots(existingSecuritySnapshots)
        }
    }, [securitySnapshotsSubscriptionResponse])

    useEffect(() => {
        if (securitySnapshots && securitySnapshots.length > 0) {
            const newSecurityCodeToSnapshot = new Map([...securityCodeToSnapshot])
            securitySnapshots.forEach((securitySnapshot) => {
                securityCodeToPreviousClose.current.set(securitySnapshot.security!.code, securitySnapshot.previousClose)
                newSecurityCodeToSnapshot.set(getSecurityKey(securitySnapshot.security?.exchange ?? 1, securitySnapshot.security?.code ?? ''), securitySnapshot)
            })
            setSecurityCodeToSnapshot(newSecurityCodeToSnapshot)
        }
    }, [securitySnapshots])

    const fetchSecuritySnapshotBulk = useCallback(
        async (securities: Security[]) => {
            try {
                const result: SecuritySnapshots = await fetchSecuritySnapshotsBulk({
                    securities: Array.from(securities),
                    dispatch: dispatch
                })
                setSecuritySnapshots(result.securitySnapshots)
            } catch (error) {
                console.error('Error fetching security data', error)
                return
            }
        },
        [dispatch]
    )

    useEffect(() => {
        const securities: Security[] = []
        basketAnalyzeDetails?.basketPositions?.forEach((position) => {
            if (position.security) {
                securities.push(position.security)
                // Position.security carries only exchange/code; the full instrument lives in the securities cache.
                const underlyingSecurityCode = getSecurityFromCache(getSecurityKey(position.security.exchange, position.security.code))?.underlyingSecurityCode
                if (underlyingSecurityCode) {
                    securities.push({
                        exchange: position.security.exchange,
                        code: underlyingSecurityCode
                    })
                }
            }
        })
        if (securities.length > 0) {
            fetchSecuritySnapshotBulk(securities)
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                        securities: securities.map((sec) => ({
                            exchange: sec.exchange,
                            code: sec.code
                        }))
                    }
                ],
                candleSubscriptions: [
                    {
                        tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                        timeBucket: {
                            timeSeries: TimeBucket_TimeSeries.SECOND,
                            value: 1
                        },
                        securities: securities
                            .filter((sec) => sec.segment === Segment.FO)
                            .map((sec) => ({
                                exchange: sec.exchange,
                                code: sec.code
                            }))
                    }
                ]
            })
        }
    }, [dispatch, fetchSecuritySnapshotBulk, basketAnalyzeDetails])

    useEffect(() => {
        if (ws !== null) {
            ws!.addListener(onWebSocketMessageReceived)
        }
        return () => {
            if (ws !== null) {
                ws!.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    const handleCustomSquareOffClick = useCallback(async (position: Position, index: number) => {
        if(!basketAnalyzeDetails) return
        const type = position?.closePosition > 0 ? 'buy' : 'sell'
        const analyzeOrderIndex = basketAnalyzeDetails?.positionOrders.findIndex((order) => (order.type === type && order.security?.code === position.security?.code))
        if(analyzeOrderIndex !== -1){
            const squareOffOrder = basketAnalyzeDetails?.positionParentSqrOffOrders[analyzeOrderIndex]
            if(squareOffOrder && optionsExpiries){
                const newOrders = [...orders]
                newOrders.push({...squareOffOrder, readonly: false})
                getCurentOrderPricesFromData(newOrders, optionsExpiries, underlyingToken, []).then((orderPrices) => {
                    setOrderDataFunction({orders: newOrders, orderPrices})
                })
            }
        }
    }, [basketAnalyzeDetails, optionsExpiries, orders, underlyingToken])

    const findSquaredOffOrder = useCallback((position: Position) => {  
        if(!basketAnalyzeDetails) return
        const type = position?.closePosition > 0 ? 'sell' : 'buy'
        return orders.find((order) => (order.type === type && order.security?.code === position.security?.code))
    }, [orders, basketAnalyzeDetails])

    return basketAnalyzeDetails ? (
        <>
            <TableHead>
                <TableRow sx={customPositionRowStyle}>
                    <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}></TableCell>
                    <TableCell style={{ width: '35%', padding: '5px' }}>
                        <Typography variant="caption" color={'textSecondary'}>
                            SYMBOL
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}></TableCell>
                    <TableCell
                        style={{
                            ...customPositionCellStyle,
                            width: showGreeks ? 'auto' : '9%'
                        }}
                    >
                        <Typography variant="caption" color={'textSecondary'}>
                            {showGreeks ? 'DELTA' : 'QTY'}
                        </Typography>
                    </TableCell>
                    <TableCell
                        style={{
                            ...customPositionCellStyle,
                            width: showGreeks ? 'auto' : '18%'
                        }}
                    >
                        <Typography variant="caption" color={'textSecondary'}>
                            {showGreeks ? 'THETA' : 'AVG'}
                        </Typography>
                    </TableCell>
                    <TableCell
                        style={{
                            ...customPositionCellStyle,
                            width: showGreeks ? 'auto' : '18%'
                        }}
                    >
                        <Typography variant="caption" color={'textSecondary'}>
                            {showGreeks ? 'GAMMA' : 'LTP/SQP'}
                        </Typography>
                    </TableCell>
                    <TableCell
                        style={{
                            ...customPositionCellStyle,
                            width: showGreeks ? 'auto' : '18%'
                        }}
                    >
                        <Typography variant="caption" color={'textSecondary'}>
                            {showGreeks ? 'VEGA' : 'P&L'}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {[...basketWisePositions!]
                    .sort((a, b) => (strikePriceOf(a) ?? 0) - (strikePriceOf(b) ?? 0))
                    .map((position, j) => {
                        return (
                            <BasketPositionTableRow
                                candlesData={candlesData}
                                key={`${position.security?.code} ${basketAnalyzeDetails.strategyCode} analyzation`}
                                position={position}
                                showSquareOffButton={false}
                                showSquareOffButtonOnHover={true}
                                strategyCode={basketAnalyzeDetails.strategyCode}
                                i={j}
                                squaredOffOrder={findSquaredOffOrder(position)}
                                securityCodeToSnapshot={securityCodeToSnapshot}
                                showGreeks={false}
                                basketId={basketAnalyzeDetails.id}
                                handleCustomSquareOffClick={() => handleCustomSquareOffClick(position, j)}
                                rowIndex={j}
                            />
                        )
                    })}
            </TableBody>
        </>
    ) : null
}

export default memo(ReadOnlyBasketWisePositionContainer)
